import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Sparkles, Search, RefreshCw, CheckCircle2, Clock, XCircle, Copy,
  ExternalLink, Building2, ArrowRight, X,
} from "lucide-react";

/**
 * Admin view of every Xchangely Pay payment link generated across all
 * merchants. Mirrors what the merchant sees on their portal but
 * unrestricted — staff can audit / reconcile any transaction.
 */
const STATUS_ICON = (s) => {
  const k = (s || "").toLowerCase();
  if (["completed", "settled", "paid"].includes(k))             return CheckCircle2;
  if (["failed", "expired", "rejected", "cancelled"].includes(k)) return XCircle;
  return Clock;
};
const STATUS_TONE = (s) => {
  const k = (s || "").toLowerCase();
  if (["completed", "settled", "paid"].includes(k))             return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (["failed", "expired", "rejected", "cancelled"].includes(k)) return "bg-rose-50 text-rose-700 border-rose-200";
  return "bg-amber-50 text-amber-800 border-amber-200";
};
const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } };
const fmtMoney = (n, cur) => {
  if (n == null) return "—";
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency: cur || "EUR" }).format(n); }
  catch { return `${n} ${cur || ""}`; }
};

export default function AdminXpayTransactions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const merchantIdFilter = searchParams.get("merchant_id") || "";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  // Capture the focused merchant's display name once we see a row, so the badge has a label.
  const focusedMerchantName = useMemo(() => {
    if (!merchantIdFilter) return null;
    const row = rows.find((r) => r.merchant_id === merchantIdFilter);
    return row?.merchant_business_name || row?.merchant_email || null;
  }, [merchantIdFilter, rows]);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ limit: "500" });
      if (merchantIdFilter) qs.set("merchant_id", merchantIdFilter);
      const r = await api.get(`/admin/xpay/links?${qs}`);
      setRows(r.data?.links || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load transactions");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [merchantIdFilter]);

  const clearMerchantFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("merchant_id");
    setSearchParams(next);
  };

  const copyLink = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Link copied"),
      () => toast.error("Copy failed"),
    );
  };

  const filtered = rows.filter((r) => {
    if (statusFilter && (r.status || "").toLowerCase() !== statusFilter) return false;
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (r.merchant_business_name || "").toLowerCase().includes(q)
        || (r.merchant_email || "").toLowerCase().includes(q)
        || (r.customer_email || "").toLowerCase().includes(q)
        || (r.reference || "").toLowerCase().includes(q)
        || (r.provider_session_id || "").toLowerCase().includes(q)
        || (r.id || "").toLowerCase().includes(q);
  });

  const counts = {
    total:      rows.length,
    completed:  rows.filter((r) => ["completed", "settled", "paid"].includes((r.status || "").toLowerCase())).length,
    pending:    rows.filter((r) => !["completed", "settled", "paid", "failed", "expired", "rejected", "cancelled"].includes((r.status || "").toLowerCase())).length,
    failed:     rows.filter((r) => ["failed", "expired", "rejected", "cancelled"].includes((r.status || "").toLowerCase())).length,
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl">
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-50 border border-indigo-200 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-indigo-700" />
            </div>
            <div>
              <h1 className="font-display font-bold text-2xl">Xchangely Pay transactions</h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Every payment link generated across every approved merchant.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {merchantIdFilter && (
              <Button onClick={() => navigate("/admin/xpay-finance")} variant="ghost" size="sm" className="rounded-xl gap-1.5"
                       data-testid="xpay-tx-jump-finance">
                <ExternalLink className="w-3.5 h-3.5" /> Back to finance
              </Button>
            )}
            <Button onClick={load} variant="outline" size="sm" className="rounded-xl gap-1.5"
                     data-testid="xpay-tx-refresh">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
          </div>
        </div>

        {merchantIdFilter && (
          <div className="mb-4 inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-indigo-50 border border-indigo-200 text-xs"
               data-testid="xpay-tx-merchant-filter">
            <Building2 className="w-3.5 h-3.5 text-indigo-700" />
            <span className="text-indigo-900">
              Filtered to merchant:&nbsp;
              <span className="font-semibold">{focusedMerchantName || merchantIdFilter}</span>
            </span>
            <button onClick={clearMerchantFilter} className="ml-1.5 text-indigo-700 hover:bg-indigo-100 rounded p-0.5"
                    title="Show all merchants" data-testid="xpay-tx-clear-merchant">
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="xpay-tx-stats">
          {[
            { l: "Total",      v: counts.total,      c: "" },
            { l: "Completed",  v: counts.completed,  c: "text-emerald-700" },
            { l: "Pending",    v: counts.pending,    c: "text-amber-700" },
            { l: "Failed",     v: counts.failed,     c: "text-rose-700" },
          ].map((s) => (
            <div key={s.l} className="xch-card p-4">
              <div className={`text-2xl font-display font-bold ${s.c}`}>{s.v}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{s.l}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="xpay-tx-filters">
          {[
            { v: "",          l: "All" },
            { v: "initiated", l: "Initiated" },
            { v: "pending",   l: "Pending" },
            { v: "completed", l: "Completed" },
            { v: "failed",    l: "Failed" },
          ].map((s) => (
            <button key={s.v}
                    onClick={() => setStatusFilter(s.v)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors
                      ${statusFilter === s.v
                        ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                        : "bg-background border-border text-foreground hover:bg-muted"}`}
                    data-testid={`xpay-tx-tab-${s.v || "all"}`}>
              {s.l}
            </button>
          ))}
          <div className="ml-auto relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={filter} onChange={(e) => setFilter(e.target.value)}
                    placeholder="Merchant, email, reference, session id…"
                    className="pl-9 h-9 w-80 text-xs rounded-xl"
                    data-testid="xpay-tx-search" />
          </div>
        </div>

        {/* Table */}
        <div className="xch-card overflow-hidden" data-testid="xpay-tx-table">
          {loading && <div className="p-6 text-xs text-muted-foreground">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No transactions{statusFilter ? ` with status "${statusFilter}"` : ""}.
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="divide-y divide-border">
              {filtered.map((r) => {
                const Icon = STATUS_ICON(r.status);
                return (
                  <div key={r.id} className="px-5 py-3.5 flex items-center gap-3 flex-wrap"
                        data-testid={`xpay-tx-row-${r.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-bold text-sm">
                          {fmtMoney(r.amount, r.from_currency)}
                        </span>
                        <ArrowRight className="w-3 h-3 text-muted-foreground" />
                        <span className="text-sm">{r.to_currency || "—"}</span>
                        <span className="text-[10px] text-muted-foreground">· {r.method}</span>
                        {r.reference && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-foreground font-mono">
                            {r.reference}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-0.5">
                        <Building2 className="w-3 h-3" />
                        <span className="font-semibold text-foreground/80">{r.merchant_business_name}</span>
                        <span className="text-muted-foreground">· {r.merchant_email}</span>
                      </div>
                      {r.customer_email && (
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          Customer: {r.customer_email}
                        </div>
                      )}
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        Created {fmtDate(r.created_at)}
                        {r.updated_at && ` · updated ${fmtDate(r.updated_at)}`}
                        {r.last_webhook_at && ` · webhook ${fmtDate(r.last_webhook_at)}`}
                      </div>
                    </div>
                    <Badge className={`text-[10px] uppercase tracking-wider gap-1 ${STATUS_TONE(r.status)}`} variant="outline"
                            data-testid={`xpay-tx-status-${r.id}`}>
                      <Icon className="w-3 h-3" /> {r.status || "unknown"}
                    </Badge>
                    <div className="flex items-center gap-1">
                      <Button onClick={() => copyLink(r.iframe_url || r.link_url)}
                              variant="ghost" size="sm" className="rounded-xl h-7 gap-1"
                              title="Copy share link"
                              data-testid={`xpay-tx-copy-${r.id}`}>
                        <Copy className="w-3 h-3" />
                      </Button>
                      {(r.iframe_url || r.link_url) && (
                        <Button asChild variant="ghost" size="sm" className="rounded-xl h-7 gap-1"
                                title="Open the customer-facing checkout">
                          <a href={r.iframe_url || r.link_url} target="_blank" rel="noopener noreferrer"
                              data-testid={`xpay-tx-open-${r.id}`}>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
